// SeatingPlanDialog: Sitzplan je Termin, Zuordnung von Gruppen und Teilnehmern zu Tischen
// mit PDF-Export (Anlage v1.4 Abschnitt 2.7 Punkt 8)
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  seatingService,
  SeatingPlan,
  SeatingTableData,
} from '../../../services/seatingService';

interface SeatingPlanDialogProps {
  open: boolean;
  scheduleId: number | null;
  /** Anzeigetext des Termins, z.B. "15.08.2026, 14:00 Uhr" */
  slotLabel: string;
  onClose: () => void;
}

interface TableDraft {
  name: string;
  capacity: string;
  sortOrder: string;
}

interface AssignmentDraft {
  bookingId: string;
  participants: string;
  note: string;
}

const emptyAssignmentDraft: AssignmentDraft = { bookingId: '', participants: '1', note: '' };

const SeatingPlanDialog: React.FC<SeatingPlanDialogProps> = ({ open, scheduleId, slotLabel, onClose }) => {
  const [plan, setPlan] = useState<SeatingPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTable, setNewTable] = useState<TableDraft>({ name: '', capacity: '10', sortOrder: '0' });
  const [tableDrafts, setTableDrafts] = useState<Record<number, TableDraft>>({});
  const [assignmentDrafts, setAssignmentDrafts] = useState<Record<number, AssignmentDraft>>({});

  // Sitzplan laden und Bearbeitungs-Entwürfe initialisieren
  const loadPlan = useCallback(async () => {
    if (!scheduleId) return;
    setLoading(true);
    try {
      const data = await seatingService.getSeatingPlan(scheduleId);
      setPlan(data);

      const drafts: Record<number, TableDraft> = {};
      const aDrafts: Record<number, AssignmentDraft> = {};
      data.tables.forEach((t) => {
        drafts[t.id] = { name: t.name, capacity: String(t.capacity), sortOrder: String(t.sortOrder) };
        aDrafts[t.id] = { ...emptyAssignmentDraft };
      });
      setTableDrafts(drafts);
      setAssignmentDrafts(aDrafts);
      setNewTable({ name: `Tisch ${data.tables.length + 1}`, capacity: '10', sortOrder: String(data.tables.length) });
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden des Sitzplans');
    } finally {
      setLoading(false);
    }
  }, [scheduleId]);

  useEffect(() => {
    if (open && scheduleId) {
      setError(null);
      setPlan(null);
      loadPlan();
    }
  }, [open, scheduleId, loadPlan]);

  // Aktion ausführen und Sitzplan neu laden, Fehler als Alert anzeigen
  const runAction = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await loadPlan();
    } catch (err: any) {
      setError(err.message || 'Aktion fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleCreateTable = () => {
    const capacity = parseInt(newTable.capacity, 10);
    const sortOrder = parseInt(newTable.sortOrder, 10);
    if (!newTable.name.trim()) {
      setError('Bitte einen Tischnamen angeben.');
      return;
    }
    if (!capacity || capacity < 1) {
      setError('Die Kapazität muss mindestens 1 betragen.');
      return;
    }
    runAction(() => seatingService.createTable(scheduleId!, {
      name: newTable.name.trim(),
      capacity,
      sortOrder: isNaN(sortOrder) ? 0 : sortOrder,
    }));
  };

  const handleUpdateTable = (tableId: number) => {
    const draft = tableDrafts[tableId];
    if (!draft) return;
    const capacity = parseInt(draft.capacity, 10);
    const sortOrder = parseInt(draft.sortOrder, 10);
    if (!draft.name.trim()) {
      setError('Bitte einen Tischnamen angeben.');
      return;
    }
    if (!capacity || capacity < 1) {
      setError('Die Kapazität muss mindestens 1 betragen.');
      return;
    }
    runAction(() => seatingService.updateTable(tableId, {
      name: draft.name.trim(),
      capacity,
      sortOrder: isNaN(sortOrder) ? 0 : sortOrder,
    }));
  };

  const handleDeleteTable = (tableId: number) => {
    runAction(() => seatingService.deleteTable(tableId));
  };

  const handleAddAssignment = (tableId: number) => {
    const draft = assignmentDrafts[tableId] || emptyAssignmentDraft;
    const bookingId = parseInt(draft.bookingId, 10);
    const participants = parseInt(draft.participants, 10);
    if (!bookingId) {
      setError('Bitte eine Buchung auswählen.');
      return;
    }
    if (!participants || participants < 1) {
      setError('Es muss mindestens 1 Person zugeordnet werden.');
      return;
    }
    runAction(() => seatingService.createAssignment(tableId, {
      bookingId,
      participants,
      note: draft.note.trim() ? draft.note.trim() : null,
    }));
  };

  const handleDeleteAssignment = (assignmentId: number) => {
    runAction(() => seatingService.deleteAssignment(assignmentId));
  };

  const handleDownloadPdf = async () => {
    if (!scheduleId || !plan) return;
    setBusy(true);
    setError(null);
    try {
      const dateLabel = plan.date ? plan.date.substring(0, 10) : String(scheduleId);
      await seatingService.downloadSeatingPlanPdf(scheduleId, dateLabel);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Erstellen des Sitzplan-PDFs');
    } finally {
      setBusy(false);
    }
  };

  const updateTableDraft = (tableId: number, field: keyof TableDraft, value: string) => {
    setTableDrafts((prev) => ({
      ...prev,
      [tableId]: { ...(prev[tableId] || { name: '', capacity: '', sortOrder: '' }), [field]: value },
    }));
  };

  const updateAssignmentDraft = (tableId: number, field: keyof AssignmentDraft, value: string) => {
    setAssignmentDrafts((prev) => ({
      ...prev,
      [tableId]: { ...(prev[tableId] || emptyAssignmentDraft), [field]: value },
    }));
  };

  // Buchungen mit noch offenen Personen (für die Zuordnungs-Auswahl)
  const availableBookings = plan?.bookings.filter((b) => b.remainingCount > 0) || [];
  const unassignedGroups = availableBookings;

  const renderTable = (table: SeatingTableData) => {
    const draft = tableDrafts[table.id] || { name: table.name, capacity: String(table.capacity), sortOrder: String(table.sortOrder) };
    const aDraft = assignmentDrafts[table.id] || emptyAssignmentDraft;

    return (
      <Box key={table.id} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
        {/* Tisch-Kopf: Name, Kapazität, Sortierung, Speichern, Löschen */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            label="Tischname"
            size="small"
            value={draft.name}
            onChange={(e) => updateTableDraft(table.id, 'name', e.target.value)}
            inputProps={{ maxLength: 50 }}
            sx={{ width: 180 }}
          />
          <TextField
            label="Kapazität"
            size="small"
            type="number"
            value={draft.capacity}
            onChange={(e) => updateTableDraft(table.id, 'capacity', e.target.value)}
            inputProps={{ min: 1 }}
            sx={{ width: 110 }}
          />
          <TextField
            label="Sortierung"
            size="small"
            type="number"
            value={draft.sortOrder}
            onChange={(e) => updateTableDraft(table.id, 'sortOrder', e.target.value)}
            sx={{ width: 110 }}
          />
          <IconButton
            color="primary"
            onClick={() => handleUpdateTable(table.id)}
            title="Tisch speichern"
            disabled={busy}
          >
            <SaveIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDeleteTable(table.id)}
            title="Tisch löschen (samt Zuordnungen)"
            disabled={busy}
          >
            <DeleteIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            label={`Belegt: ${table.occupied} / ${table.capacity}`}
            color={table.occupied > table.capacity ? 'error' : table.occupied === table.capacity ? 'warning' : 'success'}
            size="small"
          />
        </Box>

        {/* Zuordnungen des Tisches */}
        {table.assignments.length > 0 && (
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Gruppe</TableCell>
                <TableCell>Buchungsnr.</TableCell>
                <TableCell align="right">Personen</TableCell>
                <TableCell>Notiz</TableCell>
                <TableCell align="center">Entfernen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {table.assignments.map((assignment) => (
                <TableRow key={assignment.id}>
                  <TableCell>{assignment.customerName}</TableCell>
                  <TableCell>{assignment.bookingNumber}</TableCell>
                  <TableCell align="right">{assignment.participants}</TableCell>
                  <TableCell>{assignment.note || ''}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteAssignment(assignment.id)}
                      title="Zuordnung entfernen"
                      disabled={busy}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Neue Zuordnung: Buchung, Personenzahl, Notiz */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Buchung zuordnen</InputLabel>
            <Select
              value={aDraft.bookingId}
              label="Buchung zuordnen"
              onChange={(e) => updateAssignmentDraft(table.id, 'bookingId', String(e.target.value))}
            >
              {availableBookings.length === 0 && (
                <MenuItem value="" disabled>
                  Keine Gruppen mit offenen Personen
                </MenuItem>
              )}
              {availableBookings.map((booking) => (
                <MenuItem key={booking.id} value={String(booking.id)}>
                  {booking.customerName} ({booking.bookingNumber}), Rest {booking.remainingCount}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Personen"
            size="small"
            type="number"
            value={aDraft.participants}
            onChange={(e) => updateAssignmentDraft(table.id, 'participants', e.target.value)}
            inputProps={{ min: 1 }}
            sx={{ width: 100 }}
          />
          <TextField
            label="Notiz"
            size="small"
            value={aDraft.note}
            onChange={(e) => updateAssignmentDraft(table.id, 'note', e.target.value)}
            inputProps={{ maxLength: 200 }}
            sx={{ flexGrow: 1, minWidth: 160 }}
          />
          <IconButton
            color="primary"
            onClick={() => handleAddAssignment(table.id)}
            title="Zuordnung hinzufügen"
            disabled={busy || availableBookings.length === 0}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Sitzplan{slotLabel ? `, ${slotLabel}` : ''}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && plan && (
          <>
            {/* Tische mit Zuordnungen */}
            <Typography variant="h6" sx={{ mb: 1 }}>Tische</Typography>
            {plan.tables.length === 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Noch keine Tische angelegt. Legen Sie unten den ersten Tisch an.
              </Typography>
            )}
            {plan.tables.map(renderTable)}

            {/* Neuen Tisch anlegen */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Neuer Tisch"
                size="small"
                value={newTable.name}
                onChange={(e) => setNewTable((prev) => ({ ...prev, name: e.target.value }))}
                inputProps={{ maxLength: 50 }}
                sx={{ width: 180 }}
              />
              <TextField
                label="Kapazität"
                size="small"
                type="number"
                value={newTable.capacity}
                onChange={(e) => setNewTable((prev) => ({ ...prev, capacity: e.target.value }))}
                inputProps={{ min: 1 }}
                sx={{ width: 110 }}
              />
              <TextField
                label="Sortierung"
                size="small"
                type="number"
                value={newTable.sortOrder}
                onChange={(e) => setNewTable((prev) => ({ ...prev, sortOrder: e.target.value }))}
                sx={{ width: 110 }}
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleCreateTable}
                disabled={busy}
              >
                Tisch anlegen
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Nicht zugeordnete Gruppen mit Restpersonen */}
            <Typography variant="h6" sx={{ mb: 1 }}>Nicht zugeordnete Gruppen</Typography>
            {unassignedGroups.length === 0 ? (
              <Alert severity="success">Alle Gruppen sind vollständig zugeordnet.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Gruppe</TableCell>
                    <TableCell>Buchungsnr.</TableCell>
                    <TableCell align="right">Personen</TableCell>
                    <TableCell align="right">Zugeordnet</TableCell>
                    <TableCell align="right">Offen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unassignedGroups.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>{booking.customerName}</TableCell>
                      <TableCell>{booking.bookingNumber}</TableCell>
                      <TableCell align="right">{booking.personCount}</TableCell>
                      <TableCell align="right">{booking.assignedCount}</TableCell>
                      <TableCell align="right">
                        <Chip label={booking.remainingCount} color="warning" size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleDownloadPdf}
          startIcon={<PdfIcon />}
          disabled={busy || loading || !plan}
        >
          Sitzplan als PDF
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeatingPlanDialog;
